/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import assertUnreachable from '../../lang/assertUnreachable';
import Disposable from '../../lang/Disposable';
import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import SDK from '../SDK';
import {StreamSetupListener} from '../../dom/StreamSetupListener';
import GarbageCollectorManager from '../../dom/GarbageCollectorManager';
import FeatureEnablement from '../../environment/FeatureEnablement';
import {AddIceCandidatesStatus, SetRemoteDescriptionStatus} from '../discovery/EndPoint';
import Discovery from '../discovery/Discovery';
import IPeerConnection from '../../rtc/IPeerConnection';
import PeerConnectionService from '../../rtc/PeerConnectionService';
import PeerConnectionContext from '../context/PeerConnectionContext';
import RtcPublishMonitor from '../../rtc/RtcPublishMonitor';

import RtcConfigurationManager from '../../rtc/RtcConfigurationManager';
import PublisherState from './PublisherState';
import PublisherContext from './PublisherContext';

const iceCandidateAccumulationInterval = 100;
const softRestartInterval = 1000;

export default class RealTimePublisher {
  private readonly _logger: ILogger = LoggerFactory.getLogger('RealTimePublisher');
  private readonly _publisherContext: PublisherContext;
  private readonly _peerConnectionContext: PeerConnectionContext;
  private readonly _handleStreamFailure: () => Promise<void>;

  constructor(publisherContext: PublisherContext, peerConnectionContext: PeerConnectionContext, handleStreamFailure: () => Promise<void>) {
    this._publisherContext = publisherContext;
    this._peerConnectionContext = peerConnectionContext;
    this._handleStreamFailure = handleStreamFailure;
  }

  start(uri: URL, token: string, listenOnStreamSetup: StreamSetupListener): Promise<void> {
    if (!GlobalScope.value.navigatorIsOnLine) {
      this._publisherContext.state.value = PublisherState.Offline;

      return Promise.resolve();
    }

    const mediaStream = this._peerConnectionContext.mediaStream.value;

    if (!mediaStream) {
      this._logger.error('Publish failed no media stream provided');
      this._publisherContext.state.value = PublisherState.ConfigurationError;

      return Promise.resolve();
    }

    const mediaTracks = mediaStream.getTracks();
    const trackKinds = mediaTracks.reduce((accumulator: ('audio' | 'video')[], track) => {
      if (track.kind === 'audio' || track.kind === 'video') {
        accumulator.push(track.kind);
      } else {
        this._logger.warn('[%s] Track kind is not supported', track.kind);
      }

      return accumulator;
    }, []);

    return Promise.all([
      Discovery.discoverClosestEndPointWithCaching(uri),
      PeerConnectionService.createPeerConnectionOffer('sendonly', trackKinds)
        .then(({localOffer, peerConnection}) => {
          this._peerConnectionContext.peerConnection.value = peerConnection;

          return {
            localOffer,
            peerConnection
          };
        })
    ])
      .then(([endPoint, {localOffer, peerConnection}]) => {
        this._publisherContext.online.value = true;
        this._publisherContext.endPoint.value = endPoint;
        this._logger.info('Connecting to [%s]', endPoint.toString());
        this._logger.info('Local offer:\n' + localOffer.sdp);

        if (FeatureEnablement.clientOfferDisabled || !peerConnection.supportsSetConfiguration || !peerConnection.supportsGetConfiguration || this._publisherContext.failureCount.value > 0) {
          peerConnection.close();
          peerConnection.dispose();

          if (SDK.forceGarbageCollectionOnRestart && !(FeatureEnablement.isMobile && SDK.skipGarbageCollectionOnMobileDevices)) {
            GarbageCollectorManager.forceGarbageCollection();
          }

          this._peerConnectionContext.peerConnection.value = null;

          return endPoint.publish(this._publisherContext.name, token, null, this._publisherContext.failureCount.value);
        }

        return endPoint.publish(this._publisherContext.name, token, localOffer, this._publisherContext.failureCount.value);
      })
      .then(response => {
        const {status} = response;

        this._publisherContext.state.value = this._publisherContext.mapPublishStatusToPublisherStatus(status);

        this._publisherContext.applyPublishStatus(status);

        if (status !== 'ok') {
          this._publisherContext.stream.value = null;

          this._logger.info(
            '[%s] Publish failed with status [%s]',
            this._publisherContext.streamId,
            status
          );

          return;
        }

        const {stream, rtcConfiguration, setRemoteDescriptionResponse, createOfferDescriptionResponse, createAnswerDescriptionResponse} = response;

        this._publisherContext.stream.value = stream;

        this._logger.debug(
          '[%s] Publish completed [%s] [%j] [%j] [%j] [%j]',
          this._publisherContext.streamId,
          status,
          rtcConfiguration,
          setRemoteDescriptionResponse,
          createOfferDescriptionResponse,
          createAnswerDescriptionResponse
        );

        this._publisherContext.state.value = this._publisherContext.mapPublishStatusToPublisherStatus(status);

        this._publisherContext.applyPublishStatus(status);

        return this.applyRtcConfiguration(this._peerConnectionContext.peerConnection.value, rtcConfiguration as RTCConfiguration)
          .then(peerConnection => {
            let submitCandidatesTimeout: undefined | number;
            let cancelDiscovery = false;
            let discoveryCompleted = false;
            const candidates: RTCIceCandidate[] = [];

            this._peerConnectionContext.peerConnection.value = peerConnection;

            peerConnection.addStream(this._peerConnectionContext.mediaStream.value as MediaStream);

            peerConnection.onicecandidate = (e): void => {
              if (this._publisherContext.stream.value !== stream) {
                return;
              }

              if (this._peerConnectionContext.peerConnection.value !== peerConnection) {
                return;
              }

              if (cancelDiscovery) {
                return;
              }

              if (!SDK.sendLocalCandidates.value) {
                return;
              }

              if (e.candidate && e.candidate.candidate) {
                candidates.push(e.candidate);
              } else {
                discoveryCompleted = true;
              }

              if (!submitCandidatesTimeout) {
                submitCandidatesTimeout = GlobalScope.value.setTimeout(() => {
                  if (this._publisherContext.stream.value !== stream) {
                    return;
                  }

                  if (cancelDiscovery) {
                    return;
                  }

                  const ignored = this._publisherContext.endPoint.value?.addIceCandidates(stream, candidates, discoveryCompleted)
                    .then(({status, options}) => {
                      if (status !== 'ok') {
                        if (this._publisherContext.stream.value !== stream) {
                          return;
                        }

                        if (this._peerConnectionContext.peerConnection.value !== peerConnection) {
                          return;
                        }

                        this._publisherContext.state.value = this.mapAddIceCandidatesStatusToPublisherStatus(status);
                        this._publisherContext.applyAddIceCandidatesStatus(status);

                        this._logger.warn('[%s] Failed to add ICE candidates with reason [%s]', this._publisherContext.streamId, status);

                        const ignored = this._handleStreamFailure()
                          .catch(e => {
                            this._logger.error(
                              '[%s] Failed handling stream failure after peer connection failed to add ICE candidates with reason [%s]',
                              this._publisherContext.streamId,
                              status,
                              e
                            );
                          });

                        return;
                      }

                      if (options?.includes('cancel')) {
                        cancelDiscovery = true;
                      }

                      this._logger.info('[%s] Added ICE candidates with reason [%s] and options [%s]', this._publisherContext.streamId, status, options);
                    })
                    .catch(e => {
                      if (this._publisherContext.stream.value !== stream) {
                        return;
                      }

                      if (this._peerConnectionContext.peerConnection.value !== peerConnection) {
                        return;
                      }

                      this._logger.error('[%s] Failed to add ICE candidates', this._publisherContext.streamId, e);
                      this._publisherContext.state.value = PublisherState.ConnectionError;
                      this._publisherContext.applyUnhandledError();

                      const ignored = this._handleStreamFailure()
                        .catch(e => {
                          this._logger.error(
                            '[%s] Failed handling stream failure after peer connection failed to add ICE candidates',
                            this._publisherContext.streamId,
                            e
                          );
                        });
                    });
                }, iceCandidateAccumulationInterval);
              }
            };

            peerConnection.oniceconnectionstatechange = (): void => {
              if (this._publisherContext.stream.value !== stream) {
                return;
              }

              if (this._peerConnectionContext.peerConnection.value !== peerConnection) {
                return;
              }

              const retryCallback = (): void => {
                // If we stop normally the peer connection is unregistered first.
                // Thus anytime we see a closed peer connection that is still valid, it is an error.
                this._publisherContext.state.value = PublisherState.ConnectionError;
                this._publisherContext.publishing.value = false;
                this._publisherContext.loading.value = true;

                const ignored = this._handleStreamFailure()
                  .catch(e => {
                    this._logger.error(
                      '[%s] Failed handling stream failure after peer connection stopped with state [%s]',
                      this._publisherContext.streamId,
                      peerConnection.iceConnectionState,
                      e
                    );
                  });
              };

              switch (peerConnection.iceConnectionState) {
                case 'checking':
                case 'completed':
                case 'connected':
                case 'new':
                  return;

                case 'disconnected':
                case 'failed':
                  this._logger.info('[%s] ICE connection state changed to [%s], trying to reconnect', this._publisherContext.streamId, peerConnection.iceConnectionState);
                  this.reconnectPeerConnection(peerConnection, retryCallback);

                  return;
                case 'closed':
                  this._logger.info('[%s] ICE connection state changed to [%s], retrying to connect', this._publisherContext.streamId, peerConnection.iceConnectionState);
                  retryCallback();

                  return;
                default:
                  assertUnreachable(peerConnection.iceConnectionState as never);
              }
            };

            return new Promise<void>(resolve => {
              resolve();
            }).then(() => {
              if (!setRemoteDescriptionResponse) {
                return;
              }

              this._logger.info('[%s] Set local SDP offer [%s]', this._publisherContext.streamId, setRemoteDescriptionResponse.sessionDescription.sdp);

              return peerConnection.setLocalDescription(setRemoteDescriptionResponse.sessionDescription)
                .catch(e => {
                  this._logger.info('[%s] Failed to set local description [%j] with message [%s]', this._publisherContext.streamId, setRemoteDescriptionResponse.sessionDescription, e.message);

                  throw e;
                });
            }).then(() => {
              if (!createAnswerDescriptionResponse) {
                return;
              }

              this._logger.info('[%s] Set remote SDP answer [%s]', this._publisherContext.streamId, createAnswerDescriptionResponse?.sessionDescription?.sdp);

              return peerConnection.setRemoteDescription(createAnswerDescriptionResponse.sessionDescription)
                .catch(e => {
                  this._logger.info('[%s] Failed to set remote description [%j] with message [%s]', this._publisherContext.streamId, createAnswerDescriptionResponse?.sessionDescription, e.message);

                  throw e;
                });
            }).then(() => {
              if (!createOfferDescriptionResponse) {
                return;
              }

              this._logger.info('[%s] Set remote SDP offer [%s]', this._publisherContext.streamId, createOfferDescriptionResponse.sessionDescription.sdp);

              return peerConnection.setRemoteDescription(createOfferDescriptionResponse.sessionDescription)
                .catch(e => {
                  this._logger.info('[%s] Failed to set remote description [%j] with message [%s]', this._publisherContext.streamId, createAnswerDescriptionResponse?.sessionDescription, e.message);

                  throw e;
                })
                .then(() => {
                  return peerConnection.createAnswer({
                    offerToReceiveAudio: false,
                    offerToReceiveVideo: false
                  });
                }).then(answer => {
                  this._logger.info('[%s] Set local SDP answer [%s]', this._publisherContext.streamId, answer.sdp);

                  const endPoint = this._publisherContext.endPoint.value;

                  if (!endPoint) {
                    throw new Error('Failed to set remote description, EndPoint is not set');
                  }

                  return endPoint.setRemoteDescription(stream, answer);
                }).then(({status, sessionDescription}) => {
                  this._publisherContext.state.value = this.mapSetRemoteDescriptionStatusToPublisherStatus(status);

                  this._publisherContext.applySetRemoteDescriptionStatus(status);

                  if (status !== 'ok') {
                    return;
                  }

                  this._logger.info('[%s] Set local SDP [%s]', this._publisherContext.streamId, sessionDescription?.sdp);

                  return peerConnection.setLocalDescription(sessionDescription as RTCSessionDescription)
                    .catch(e => {
                      this._logger.info('[%s] Failed to set local description [%j] with message [%s]', this._publisherContext.streamId, sessionDescription, e.message);

                      throw e;
                    });
                });
            }).then(() => {
              listenOnStreamSetup.success(this._publisherContext.streamId);

              if (this._publisherContext.state.value !== PublisherState.Starting) {
                return Promise.resolve();
              }

              this._publisherContext.state.value = PublisherState.Publishing;

              this._publisherContext.publishing.value = true;

              const rtcPublishMonitor = new RtcPublishMonitor(
                peerConnection,
                this._publisherContext.endPoint.value?.roundTripTime ? this._publisherContext.endPoint.value.roundTripTime / 4 : 0,
                SDK.applicationActivityMonitor!.isForeground
              );

              this._publisherContext.disposables.add(rtcPublishMonitor);

              const ignored = rtcPublishMonitor.rtcStatistic.subscribe(statistics => {
                this._publisherContext.rtcStatistics.value = statistics;
              });

              return Promise.resolve();
            });
          });
      });
  }

  private async applyRtcConfiguration(
    optionalPeerConnection: IPeerConnection | null,
    rtcConfiguration: RTCConfiguration): Promise<IPeerConnection> {
    if (!optionalPeerConnection) {
      rtcConfiguration = RtcConfigurationManager.truncateIceServers(rtcConfiguration);

      return SDK.peerConnectionFactory.value.createPeerConnection(rtcConfiguration);
    }

    const newRtcConfiguration = {
      ...optionalPeerConnection.getConfiguration(),
      ...rtcConfiguration
    };

    optionalPeerConnection.setConfiguration(newRtcConfiguration);

    return optionalPeerConnection;
  }

  private reconnectPeerConnection(peerConnection: IPeerConnection, retryCallback: () => void): void {
    if (peerConnection.iceConnectionState === 'closed') {
      return;
    }

    this._publisherContext.state.value = PublisherState.Reconnecting;

    if (this._peerConnectionContext.peerConnectionReconnectAttempts < SDK.maximalNumberOfPeerConnectionReconnectAttempts || !SDK.automaticallyReconnectPeerConnection) {
      this._peerConnectionContext.peerConnectionReconnectAttempts++;

      const isClientOfferFlow = peerConnection.currentLocalDescription?.type === 'offer';

      if (FeatureEnablement.clientOfferDisabled ||
        !peerConnection.supportsSetConfiguration ||
        !peerConnection.supportsGetConfiguration ||
        !isClientOfferFlow
      ) {
        const reconnectPeerConnectionTimeout = GlobalScope.value.setTimeout(() => {
          this.reconnectPeerConnection(peerConnection, retryCallback);
        }, softRestartInterval);

        this._publisherContext.disposables.add(new Disposable(() => GlobalScope.value.clearTimeout(reconnectPeerConnectionTimeout)));

        return;
      }

      this._logger.info('Reconnecting peer connection by restarting ICE');

      const currentLocalDescription = peerConnection.currentLocalDescription;
      const ignored = peerConnection.createOffer({iceRestart: true}).then(offer => {
        return peerConnection.setLocalDescription(offer).then(() => {
          return peerConnection.setLocalDescription(currentLocalDescription)
            .then(() => {
              if (peerConnection.iceConnectionState === 'disconnected' || peerConnection.iceConnectionState === 'failed') {
                const reconnectPeerConnectionTimeout = GlobalScope.value.setTimeout(() => {
                  this.reconnectPeerConnection(peerConnection, retryCallback);
                }, softRestartInterval);

                this._publisherContext.disposables.add(new Disposable(() => GlobalScope.value.clearTimeout(reconnectPeerConnectionTimeout)));
              } else {
                this._publisherContext.state.value = PublisherState.Publishing;
              }
            });
        });
      })
        .catch(e => {
          this._logger.error('Failed to reconnect peer connection', e);
          this._peerConnectionContext.peerConnectionReconnectAttempts = 0;
          retryCallback();
        });

      return;
    }

    this._logger.info('Failed to reconnect peer connection after [%s] attempts, performing full recovery', this._peerConnectionContext.peerConnectionReconnectAttempts);
    this._peerConnectionContext.peerConnectionReconnectAttempts = 0;
    retryCallback();
  }

  private mapSetRemoteDescriptionStatusToPublisherStatus(status: SetRemoteDescriptionStatus): PublisherState {
    switch (status) {
      case 'ok':
        return PublisherState.Starting;
      case 'unauthorized':
        return PublisherState.Unauthorized;
      case 'not-found':
        return PublisherState.NotFound;
      case 'capacity':
      case 'rate-limited':
      case 'timeout':
        return PublisherState.Recovering;
      case 'failed':
        return PublisherState.Error;
      default:
        assertUnreachable(status);
    }
  }

  private mapAddIceCandidatesStatusToPublisherStatus(status: AddIceCandidatesStatus): PublisherState {
    switch (status) {
      case 'ok':
        return this._publisherContext.state.value;
      case 'unauthorized':
        return PublisherState.Unauthorized;
      case 'not-found':
        return PublisherState.NotFound;
      case 'capacity':
      case 'rate-limited':
      case 'timeout':
        return PublisherState.Recovering;
      case 'failed':
        return PublisherState.Error;
      default:
        assertUnreachable(status);
    }
  }
}